import { fg } from '@atlaskit/platform-feature-flags';

import { sha256Hash } from '../utils';

import type { commonAttributesType, NLPSearchResultSource } from './types';

// list from https://atlassian.slack.com/archives/C08VA0N40A2/p1749172242825969?thread_ts=1749088363.059769&cid=C08VA0N40A2
const products1PList = [
	'Assets',
	'Atlas',
	'Confluence',
	'Atlassian Analytics',
	'Atlassian Cloud',
	'Automation for Jira',
	'Bamboo',
	'Bitbucket',
	'Compass',
	'Jira',
	'Jira Product Discovery',
	'Jira Service Management',
	'Jira Software',
	'Jira Work Management',
	'Jira Align',
	'Migration',
	'Loom',
	'Opsgenie',
	'Platform Experiences',
	'Rovo',
	'Statuspage',
	'Trello',
	'webSearch',
	'web-page',
	'Fisheye and Crucible',
	'Crowd',
	'Sourcetree',
	'Unknown',
	'Focus',
	'file-upload',
	'Advanced Roadmaps For Jira',
	'Questions for Confluence',
	'csm',
	'Dewey Support',
	'Dewey JAC Support',
	'Help Aggregator',
];

const noAnswerPhrases = [
	"i'm sorry",
	'i apologize',
	"couldn't find ",
	"couldn't generate",
	'provide more context',
	"don't have enough information",
];

export const isNoAnswerText = (text: string): boolean => {
	const lowerCaseText = text.toLowerCase();
	return noAnswerPhrases.some((phrase) => lowerCaseText.includes(phrase));
};

type SourceProducts = {
	[productName: string]: number;
};

const getSourceProducts = (sources: NLPSearchResultSource[]) => {
	const products: SourceProducts = {};
	sources.forEach((source) => {
		const productName = source.product;
		if (productName) {
			products[productName] = (products[productName] ?? 0) + 1;
		}
	});

	return products;
};

const sanitizeSources = (sources: NLPSearchResultSource[]): { id: string; type: string }[] =>
	sources.map((source) => ({
		id: source.ari ?? source.id,
		type: source.type,
	}));

export const filter3PSources = (sources: NLPSearchResultSource[]): NLPSearchResultSource[] =>
	sources.filter((source) =>
		source.product
			? !products1PList.includes(source.product) && !source.product.includes('Plugin')
			: false,
	);

/**
 * Any keys that exactly match an entry in this Set will _NOT_ be sanitized. So be sure that anything
 * you add here will not contain PII or UGC
 *
 * Exported for unit tests: `analytics-helpers-test` keeps a duplicate list that must match — so
 * extending the allowlist requires updating that test (and review).
 */
export const ALLOWED_EXTRA_ATTRIBUTES_KEYS = new Set([
	'contentId',
	'readingAidsSessionId',
	'fieldType',
	'multipleUserResultsShown',
	'numAddtlWhoMatches',
	'isCuratedDefinition',
	'isPrecomputedDefinition',
	'editDistance',
	'bookmarkId',
	'bookmarkState',
	'dwellTime',
	'availableProducts',
	'isDescriptionEmpty',

	// from extraAPIAnalyticsAttributes
	'message_id',
	'conversational_channel_id',
	'experience_id',
	'user_id',
	// Deduplicated list of plugin names and minion IDs invoked during the AI answer generation
	'pluginsInvoked',

	// from extraAnalyticsAttributes
	'experiments',
	'featureGates',
	'isFullPageSearch',
	'has3pProducts',
	'is3pSearch',
	'mode',
	'triggeredBy',
	'referrer',
	'requestId',
]);

type ExtraAttributes = {
	[key: string]: any;
};
export const sanitizeExtraAttributes: (extraAttributes: ExtraAttributes) => ExtraAttributes = (
	extraAttributes: ExtraAttributes,
) => {
	if (!fg('kd_fe_sanitize_analytics')) {
		return extraAttributes;
	}

	const sanitizedAttributes: ExtraAttributes = {};
	// Sanitize everything except COMMON_ALLOWED_KEYS
	for (const key in extraAttributes) {
		if (ALLOWED_EXTRA_ATTRIBUTES_KEYS.has(key)) {
			sanitizedAttributes[key] = extraAttributes[key];
		} else {
			// replacing with text because the value has type `any`
			sanitizedAttributes[key] = '[sanitized]';
		}
	}

	return sanitizedAttributes;
};

/**
 * Keys produced by `filterCommonAttributes` (including optional and feature-gated fields). Keep in
 * sync when adding new attributes there — `SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST` is
 * derived from this list plus `ALLOWED_EXTRA_ATTRIBUTES_KEYS`, `location`, and `source`.
 */
const FILTER_COMMON_ATTRIBUTES_OUTPUT_KEYS: readonly string[] = [
	'searchSessionId',
	'queryHash',
	'baseQueryHash',
	'wordCount',
	'queryLength',
	'answerHash',
	'appliedFilters',
	'answerFormat',
	'answerLength',
	'answerWordCount',
	'answerCardType',
	'followUpsCount',
	'followUpsLength',
	'has3PSources',
	'sources',
	'sourceProducts',
	'apiSource',
	'brand',
	'sessionId',
	'isHello',
	'sourceProduct',
	'isReadingAids',
	'queryIntent',
	'sourcesCount',
	'sources3P',
	'sources3PCount',
	'hasSources',
	'has1PSources',
	'containsNoAnswerPhrase',
];

/**
 * Only these `attributes` keys may be passed through Search AI analytics context. Everything else
 * is dropped to prevent UGC/PII from reaching linking-platform analytics context.
 */
export const SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST = new Set<string>([
	'location',
	'source',
	...FILTER_COMMON_ATTRIBUTES_OUTPUT_KEYS,
	...ALLOWED_EXTRA_ATTRIBUTES_KEYS,
]);

export type SearchAIAnalyticsContextDataInput = {
	attributes?: Record<string, unknown>;
	location: string;
};

/** Output of {@link sanitizeSearchAIAnalyticsContextData}; `attributes` is always a defined object (possibly empty). */
export type SearchAIAnalyticsContextSanitized = {
	attributes: Record<string, unknown>;
	location: string;
};

/**
 * Single allowlist pass for Search AI analytics context: keeps only keys in
 * {@link SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST}. Use this (via {@link SearchAIAnalyticsContext}) before
 * values reach `AnalyticsContext`.
 */
export const sanitizeSearchAIAnalyticsContextData = (
	data: SearchAIAnalyticsContextDataInput,
): SearchAIAnalyticsContextSanitized => {
	const location = data.location;
	const raw: Record<string, unknown> = { ...(data.attributes ?? {}) };
	if (raw.location === undefined) {
		raw.location = location;
	}

	const attributes: Record<string, unknown> = {};
	for (const key of Object.keys(raw)) {
		if (SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST.has(key)) {
			attributes[key] = raw[key];
		}
	}

	return { attributes, location };
};

// New instrumentation attributes must be added to this function to be included in all the events
export const filterCommonAttributes: (props: commonAttributesType) => {
	searchSessionId: string;
	queryHash: string;
	baseQueryHash: string;
	wordCount: number;
	queryLength: number;
	answerHash: string;
	appliedFilters: import('./types').parsedFiltersType | undefined;
	answerFormat: import('./types').NLPSearchResultFormat | null;
	answerLength: number;
	answerWordCount: number;
	answerCardType: import('../constants').AnswerCardType;
	followUpsCount: number;
	followUpsLength: number[];
	has3PSources: boolean;
	sources: {
		id: string;
		type: string;
	}[];
	sourcesCount?: number;
	sources3P?: {
		id: string;
		type: string;
	}[];
	sources3PCount?: number;
	hasSources?: boolean;
	has1PSources?: boolean;
	containsNoAnswerPhrase?: boolean;
	sourceProducts: SourceProducts;
	apiSource: 'assistance-service' | 'cc-search-nlp' | 'xp-knowledge-discovery';
	brand: import('..').AiBrand;
	sessionId: string;
	isHello: boolean;
} = (props: commonAttributesType) => {
	const {
		appliedFilters,
		answerFormat,
		answerLength,
		answerWordCount,
		answerCardType,
		followUps,
		sources,
		extraAttributes,
		searchSessionId,
		queryHash,
		baseQueryHash,
		query,
		answerString,
		apiSource,
		brand,
		sourceProduct,
		isReadingAids,
		queryIntent,
		sessionId,
		isHello,
	} = props;

	const filtered3P = filter3PSources(sources);

	return {
		searchSessionId,
		queryHash,
		baseQueryHash,
		wordCount: (query.match(/\w+/g) || []).length,
		queryLength: query.length,
		answerHash: sha256Hash(answerString),
		appliedFilters,
		answerFormat,
		answerLength,
		answerWordCount,
		answerCardType,
		followUpsCount: followUps.length,
		followUpsLength: followUps.map((followUp) => followUp.length),
		has3PSources: filtered3P.length > 0,
		sources: sanitizeSources(sources),
		sourceProducts: getSourceProducts(sources),
		apiSource,
		brand,
		...(sourceProduct !== undefined && { sourceProduct }),
		...(isReadingAids !== undefined && { isReadingAids }),
		...(queryIntent !== undefined && { queryIntent }),
		...(fg('kd_consolidate_sources_analytics') && {
			sourcesCount: sources.length,
			sources3P: sanitizeSources(filtered3P),
			sources3PCount: filtered3P.length,
		}),
		...(fg('kd_send_no_answer_attributes') && {
			hasSources: sources.length > 0,
			has1PSources: sources.length > filtered3P.length,
			containsNoAnswerPhrase: isNoAnswerText(answerString),
		}),
		sessionId: sessionId,
		isHello,
		...sanitizeExtraAttributes(extraAttributes),
	};
};
